import type { PublicCampaign } from "@/shared/campaign/beautyCampaign";
import { BEAUTY_CAMPAIGN_CODE } from "@/shared/campaign/beautyCampaign";

function resolveBackendBase(): string {
    if (process.env.TEZAM_ONBOARD_BACKEND_URL) {
        return process.env.TEZAM_ONBOARD_BACKEND_URL.replace(/\/$/, "");
    }
    if (process.env.NEXT_PUBLIC_TEZAM_API_BASE) {
        return process.env.NEXT_PUBLIC_TEZAM_API_BASE.replace(/\/$/, "");
    }
    return "https://prod-api.tezam.by/api";
}

function asCampaign(data: unknown): PublicCampaign | null {
    if (!data || typeof data !== "object") return null;
    const row = data as PublicCampaign;
    if (typeof row.status !== "string") return null;
    return row;
}

export async function fetchPublicCampaign(code = BEAUTY_CAMPAIGN_CODE): Promise<PublicCampaign | null> {
    const url = `${resolveBackendBase()}/public/campaigns/${encodeURIComponent(code)}`;
    try {
        const res = await fetch(url, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(2500),
        });
        if (!res.ok) return null;
        return asCampaign(await res.json());
    } catch {
        return null;
    }
}

/** Active (or scheduled) campaign for industry preset — driven by platform.tezam.by /campaigns. */
export async function fetchPublicCampaignByIndustry(industryPreset: string): Promise<PublicCampaign | null> {
    const url = `${resolveBackendBase()}/public/campaigns?industry=${encodeURIComponent(industryPreset)}`;
    try {
        const res = await fetch(url, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(2500),
        });
        if (res.ok) {
            const campaign = asCampaign(await res.json());
            if (campaign && campaign.status !== "DISABLED") {
                return campaign;
            }
        }
    } catch {
        /* fall through */
    }
    // Backward-compatible fallback until backend list/industry endpoint is live.
    if (industryPreset.toUpperCase() === "BEAUTY") {
        return fetchPublicCampaign(BEAUTY_CAMPAIGN_CODE);
    }
    return null;
}

export async function fetchPublicCampaigns(): Promise<PublicCampaign[]> {
    const url = `${resolveBackendBase()}/public/campaigns`;
    try {
        const res = await fetch(url, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(2500),
        });
        if (!res.ok) return [];
        const data = await res.json();
        if (!Array.isArray(data)) {
            const single = asCampaign(data);
            return single ? [single] : [];
        }
        return data.map(asCampaign).filter((row): row is PublicCampaign => Boolean(row));
    } catch {
        return [];
    }
}

export async function fetchPublicCampaignBrowser(code = BEAUTY_CAMPAIGN_CODE): Promise<PublicCampaign | null> {
    try {
        const res = await fetch(`/api/campaigns/${encodeURIComponent(code)}`, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(2500),
        });
        if (!res.ok) return null;
        return asCampaign(await res.json());
    } catch {
        return null;
    }
}

export async function fetchPublicCampaignByIndustryBrowser(
    industryPreset: string,
): Promise<PublicCampaign | null> {
    try {
        const res = await fetch(`/api/campaigns?industry=${encodeURIComponent(industryPreset)}`, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(2500),
        });
        if (!res.ok) return null;
        return asCampaign(await res.json());
    } catch {
        return null;
    }
}
